import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2 } from "lucide-react";
import { PageIcon } from "@/components/PageIcon";

const GAP_LABEL = { no_salesperson: "No salesperson", no_agreed_date: "No agreed date", no_model: "No model",
  installation_unknown: "Installation unknown", needs_verification: "Needs verification" };

function GapsTab() {
  const [d, setD] = useState(null);
  const [gap, setGap] = useState("");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  useEffect(() => {
    setD(null);
    api.get("/sales-intel/review", { params: { start_date: `${year}-01-01`, end_date: `${year}-12-31` } }).then((r) => setD(r.data)).catch(() => setD({ error: true }));
  }, [year]);
  if (!d) return <div className="py-10 flex gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  if (d.error) return <p className="py-6 text-slate-500" data-testid="si-review-error">Not available.</p>;
  const items = gap ? d.items.filter((i) => i.gaps.includes(gap)) : d.items;
  return (
    <div data-testid="si-gaps-tab">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select value={year} onChange={(e) => setYear(e.target.value)} className="h-9 rounded-md border border-slate-300 px-2 text-sm" data-testid="si-review-year">
          {[0, 1, 2, 3].map((n) => { const y = String(new Date().getFullYear() - n); return <option key={y} value={y}>{y}</option>; })}
        </select>
        <button type="button" onClick={() => setGap("")} data-testid="si-gap-filter-all"
          className={`h-9 px-3 rounded-full text-xs border ${!gap ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600"}`}>All ({d.with_gaps})</button>
        {Object.entries(d.counts).map(([k, n]) => (
          <button key={k} type="button" onClick={() => setGap(k)} data-testid={`si-gap-filter-${k}`}
            className={`h-9 px-3 rounded-full text-xs border ${gap === k ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600"}`}>{GAP_LABEL[k]} ({n})</button>))}
      </div>
      <p className="text-xs text-slate-500 mb-3">{d.sales_considered} sale jobs considered · {d.note}</p>
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-sm" data-testid="si-gaps-table">
          <thead className="bg-slate-50 text-xs text-slate-500"><tr><th className="text-left p-2">Job</th><th className="text-left p-2">Title</th>
            <th className="text-left p-2">Created</th><th className="text-left p-2">Salesperson</th><th className="text-left p-2">Missing</th></tr></thead>
          <tbody>{items.map((i) => (
            <tr key={i.job} className="border-t border-slate-100" data-testid={`si-gap-row-${i.job}`}>
              <td className="p-2 font-mono"><Link to={i.link} className="text-indigo-700 hover:underline">{i.job}</Link></td>
              <td className="p-2 max-w-[18rem] truncate">{i.title}{i.legacy_import && <span className="ml-1 text-[10px] text-slate-400">legacy</span>}</td>
              <td className="p-2 whitespace-nowrap">{i.created}</td><td className="p-2">{i.salesperson}</td>
              <td className="p-2"><div className="flex flex-wrap gap-1">{i.gaps.map((g) => <span key={g} className="text-[10px] rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5">{GAP_LABEL[g]}</span>)}</div></td>
            </tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MappingRow({ u, onConfirm }) {
  const [pick, setPick] = useState(u.suggestions[0]?.product_legacy_id || "");
  return (
    <tr className="border-t border-slate-100" data-testid={`si-map-row-${u.woo_product_id}`}>
      <td className="p-2 font-mono">{u.woo_product_id}</td><td className="p-2">{u.name}</td><td className="p-2">{u.orders}</td>
      <td className="p-2">{u.suggestions.length ? (
        <select value={pick} onChange={(e) => setPick(e.target.value)} className="h-8 rounded-md border border-slate-300 px-2 text-xs max-w-[16rem]" data-testid={`si-map-pick-${u.woo_product_id}`}>
          {u.suggestions.map((s) => <option key={s.product_legacy_id} value={s.product_legacy_id}>{s.title} (suggested)</option>)}
        </select>) : <span className="text-xs text-slate-400">No suggestion</span>}</td>
      <td className="p-2">{!!u.suggestions.length && <Button size="sm" variant="outline" className="h-8" onClick={() => onConfirm(u.woo_product_id, pick)}
        data-testid={`si-map-confirm-${u.woo_product_id}`}>Confirm match</Button>}</td>
    </tr>
  );
}

function MappingTab() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/sales-intel/product-map").then((r) => setD(r.data)).catch(() => setD({ error: true })), []);
  useEffect(() => { load(); }, [load]);
  const confirm = (wid, pid) => api.post("/sales-intel/product-map", { woo_product_id: wid, product_legacy_id: pid })
    .then(() => { toast.success("Match confirmed (catalogue and WooCommerce unchanged)"); load(); }).catch((e) => toast.error(e.response?.data?.detail || "Failed"));
  const unmap = (wid) => api.delete(`/sales-intel/product-map/${wid}`).then(() => { toast.success("Match removed"); load(); }).catch(() => toast.error("Failed"));
  if (!d) return <div className="py-10 flex gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  if (d.error) return <p className="py-6 text-slate-500">Not available.</p>;
  return (
    <div data-testid="si-mapping-tab">
      <p className="text-xs text-slate-500 mb-3">{d.unmatched_total} WooCommerce products without a verified catalogue match · {d.note}</p>
      {!!d.mapped.length && (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50/40 p-3" data-testid="si-mapped-list">
          <p className="text-xs font-semibold text-emerald-800 mb-1">Confirmed matches</p>
          {d.mapped.map((m) => (
            <p key={m.id} className="text-xs text-slate-700">WooCommerce {m.woo_product_id} → {m.title} · by {m.by_name}
              <button type="button" className="ml-2 text-rose-600 hover:underline" onClick={() => unmap(m.woo_product_id)} data-testid={`si-unmap-${m.woo_product_id}`}>Remove</button></p>))}
        </div>)}
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-sm" data-testid="si-mapping-table">
          <thead className="bg-slate-50 text-xs text-slate-500"><tr><th className="text-left p-2">Woo ID</th><th className="text-left p-2">Woo product name</th>
            <th className="text-left p-2">Order lines</th><th className="text-left p-2">Catalogue match</th><th className="p-2" /></tr></thead>
          <tbody>{d.unmatched.map((u) => <MappingRow key={u.woo_product_id} u={u} onConfirm={confirm} />)}</tbody>
        </table>
      </div>
    </div>
  );
}

export default function SalesIntelReview() {
  const [st, setSt] = useState(null);
  useEffect(() => { api.get("/sales-intel/status").then((r) => setSt(r.data)).catch(() => setSt({})); }, []);
  if (!st) return null;
  if (!st.enabled || !st.can_confirm) return <div className="p-10 text-slate-500" data-testid="si-review-unavailable">Sales intelligence review is not available.</div>;
  return (
    <div className="px-4 sm:px-8 pt-3 sm:pt-4 pb-6 sm:pb-8 max-w-6xl" data-testid="sales-intel-review-page">
      <div className="flex items-center gap-3 mb-4"><PageIcon />
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Sales intelligence review</h1>
          <p className="text-sm text-slate-500">Optional manager review. Nothing here changes jobs, quotes, invoices, products or WooCommerce.</p>
        </div>
      </div>
      <Tabs defaultValue="gaps">
        <TabsList><TabsTrigger value="gaps" data-testid="si-tab-gaps">Data gaps</TabsTrigger>
          <TabsTrigger value="mapping" data-testid="si-tab-mapping">Product mapping</TabsTrigger></TabsList>
        <TabsContent value="gaps" className="mt-4"><GapsTab /></TabsContent>
        <TabsContent value="mapping" className="mt-4"><MappingTab /></TabsContent>
      </Tabs>
    </div>
  );
}
